/**
 * Sistema centralizado de logging
 *
 * Fornece funções padronizadas para logging com:
 * - Verificação automática de ambiente (não executa em produção)
 * - Sanitização automática de dados sensíveis
 * - Suporte a múltiplos níveis de log
 * - Envio para terminal do backend (apenas em desenvolvimento)
 */

import {
	LogCategory,
	LogLevel,
	getLogLevel,
	shouldLog,
	shouldIncludeJson,
	formatLogMessage,
	createPayloadSummary,
	isDevelopmentMode as isDevMode,
} from "@/config/logging";
import { FRONTEND_URL } from "@/config";
// Campos sensíveis - Importados do módulo centralizado de segurança
import {
	SENSITIVE_FIELDS,
	HMAC_DEBUG_ALLOWED_FIELDS,
} from "./security/sanitizers/log.sanitizer";

/**
 * Verifica se um valor parece ser um token/senha (heurística)
 */
const looksLikeSensitiveValue = (value: any): boolean => {
	if (typeof value !== "string") return false;
	const str = String(value);
	// Tokens geralmente são longos e contêm caracteres alfanuméricos e especiais
	if (str.length > 20 && /^[A-Za-z0-9+/=_-]+$/.test(str)) return true;
	// Base64-like strings
	if (str.length > 16 && /^[A-Za-z0-9+/=]+$/.test(str) && str.length % 4 === 0)
		return true;
	return false;
};

// Sanitização de dados de log - Importada do módulo centralizado de segurança
import { sanitizeLogData as sanitizeLogDataCore } from "./security/sanitizers/log.sanitizer";

/**
 * Sanitiza dados removendo ou mascarando informações sensíveis
 * Melhorado para detectar valores que parecem tokens/senhas mesmo sem campo explícito
 *
 * @param data Dados a serem sanitizados
 * @param depth Profundidade atual na recursão (limite para evitar loops infinitos)
 * @param allowHmacDebug Se true, permite campos de debug HMAC (apenas nível DEBUG)
 * @returns Dados sanitizados
 *
 * @deprecated Use import direto de '@/lib/security/sanitizers/log.sanitizer'
 * Esta função é mantida apenas para compatibilidade com código legado.
 */
const sanitizeLogData = (
	data: any,
	depth: number = 0,
	allowHmacDebug: boolean = false,
): any => {
	return sanitizeLogDataCore(data, depth, allowHmacDebug);
};

/**
 * Verifica se está em modo de desenvolvimento
 */
const isDevelopmentMode = (): boolean => {
	return isDevMode();
};

/**
 * Determina a categoria de log baseado na mensagem
 */
function detectLogCategory(message: string): LogCategory {
	const msg = message.toLowerCase();

	// Health checks
	if (msg.includes("health") || msg.includes("pong") || msg.includes("ping")) {
		return LogCategory.HEALTH;
	}

	// Autenticação
	if (
		msg.includes("hmac") ||
		msg.includes("session_token") ||
		msg.includes("auth:") ||
		msg.includes("token")
	) {
		return LogCategory.AUTH;
	}

	// Payload completo
	if (msg.includes("payload") || msg.includes("c#:payload")) {
		return LogCategory.PAYLOAD;
	}

	// Conexão
	if (
		msg.includes("corel_connection") ||
		msg.includes("connection") ||
		msg.includes("app_status")
	) {
		return LogCategory.CONNECTION;
	}

	// API
	if (
		msg.includes("api") ||
		msg.includes("request") ||
		msg.includes("response")
	) {
		return LogCategory.API;
	}

	// Erros
	if (msg.includes("error") || msg.includes("erro")) {
		return LogCategory.ERROR;
	}

	// Operações de negócio (ORDER, PROD, SNAPSHOT, etc)
	if (
		msg.includes("order") ||
		msg.includes("prod") ||
		msg.includes("snapshot") ||
		msg.includes("login") ||
		msg.includes("logout") ||
		msg.includes("pedido")
	) {
		return LogCategory.BUSINESS;
	}

	// Default: BUSINESS para logs gerais
	return LogCategory.BUSINESS;
}

/**
 * Envia logs estruturados para o console do WebView e (quando aplicável) para o endpoint do terminal do backend.
 *
 * Este logger é focado em etapas críticas do fluxo de exportação e upload, reduzindo ruído no console.
 * Atualmente só emite logs para:
 * - API_REQUEST nas rotas: /arquivos/exportarPNG, /cdr/arquivos/exportar-batch, /arquivos/layouts
 * - ACTION_RESULT para a ação "exportar-objetos-png-lite"
 * - ACTION_SEND para a ação "exportar-objetos-png-lite"
 * - ACTION_SKIP para etapas que foram intencionalmente puladas
 *
 * Para esses eventos, além do console local, é feita uma tentativa de POST em /api/terminal-log com o payload resumido.
 *
 * @param message Mensagem de log, usada também para identificação do tipo de evento (ex.: API_REQUEST, ACTION_RESULT).
 * @param data Dados auxiliares opcionais a serem serializados e enviados ao console/endpoint (payload, status, requestId, etc.).
 * @returns void
 */
const extractCallerLocation = (): string => {
	try {
		const stack = new Error().stack;
		if (!stack) return "";
		const lines = stack.split("\n").map((l) => l.trim());
		// Estratégia: pular TODOS os frames que vêm do próprio terminalLogger.ts
		// (debugLog, extractCallerLocation, e wrappers como logger.warn/terminalLog.warn).
		// O primeiro frame "externo" é o caller real.
		const isInternalFrame = (line: string) =>
			line.includes("terminalLogger.ts") ||
			line.includes("terminalLogger.js") ||
			line.includes("extractCallerLocation") ||
			line.includes("debugLog");

		const callerLine = lines.find(
			(line) => line.startsWith("at ") && !isInternalFrame(line),
		);
		if (!callerLine) return "";

		// Formatos possíveis (Chromium):
		//   "at funcName (http://host/path/file.ts?t=123:42:10)"
		//   "at http://host/path/file.ts:42:10"
		const match =
			callerLine.match(/\(([^)]+)\)$/) || callerLine.match(/at\s+(.+)$/);
		if (!match) return "";
		const raw = match[1];

		// Extrai apenas nome do arquivo + linha (ignora query string do Vite e coluna)
		const fileMatch = raw.match(/([^/\\?]+\.[a-zA-Z]+)(\?[^:]*)?:(\d+):(\d+)$/);
		return fileMatch ? `${fileMatch[1]}:${fileMatch[3]}` : raw;
	} catch {
		return "";
	}
};

export const debugLog = (message: string, data?: any) => {
	try {
		const timestamp = new Date().toLocaleTimeString("pt-BR");
		const isDebug =
			(import.meta as any)?.env?.MODE !== "production" &&
			(window as any).__DEBUG_LOGS__ !== false;
		if (!isDebug) return; // Silencioso em produção por padrão

		const caller = extractCallerLocation();
		const origin = caller ? ` [${caller}]` : "";
		const fullMessage = `🔥 [${timestamp}] [DEBUG]${origin} ${message}`;
		console.log(fullMessage);
		// Removido log de payloads complexos para limpar terminal
		// if (data !== undefined) {
		//   const sanitizedData = sanitizeLogData(data);
		//   console.log('📋 [DATA]:', sanitizedData);
		// }
	} catch {
		// No-op
	}
};

/**
 * Conjunto de funções de conveniência que padronizam níveis de log enviados ao debugLog.
 *
 * As mensagens recebem prefixos e ícones para rápida identificação no console e no endpoint de terminal.
 * Utilize estes métodos para manter consistência de logs na aplicação.
 *
 * - log: mensagens informativas gerais
 * - error: erros e exceções controladas
 * - warn: avisos relevantes que não impedem o fluxo
 * - info: informações de status
 * - debug: detalhes úteis para depuração
 */
export const terminalLog = {
	log: (message: string, data?: any) => debugLog(`[LOG] ${message}`, data),
	error: (message: string, data?: any) =>
		debugLog(`❌ [ERROR] ${message}`, data),
	warn: (message: string, data?: any) => debugLog(`⚠️ [WARN] ${message}`, data),
	info: (message: string, data?: any) => debugLog(`ℹ️ [INFO] ${message}`, data),
	debug: (message: string, data?: any) =>
		debugLog(`🐛 [DEBUG] ${message}`, data),
};

/**
 * Envia logs para o terminal do backend (apenas em desenvolvimento)
 *
 * IMPORTANTE: Esta função NÃO executa em produção por segurança.
 * Aplica filtros de nível de log configurável.
 *
 * @param message Mensagem de log
 * @param data Dados opcionais (serão sanitizados automaticamente)
 * @param category Categoria do log (opcional, será detectada automaticamente se não fornecida)
 */
export const sendToTerminal = async (
	message: string,
	data?: any,
	category?: LogCategory,
) => {
	// Verificação crítica: não enviar logs em produção
	if (!isDevelopmentMode()) {
		return;
	}

	// Detectar categoria se não fornecida
	const logCategory = category || detectLogCategory(message);

	// Mapear categoria para canal de log ANTES de processar
	// Isso permite filtrar logs por tipo usando npm run log:api, log:csharp, etc.
	let channel = "terminal"; // padrão
	let isApiLog = false;
	let isCsharpLog = false;

	if (logCategory === LogCategory.API || logCategory === LogCategory.ERROR) {
		// Logs de API e erros vão para o canal "api"
		channel = "api";
		isApiLog = true;
	} else if (
		logCategory === LogCategory.CONNECTION ||
		logCategory === LogCategory.EVENT ||
		message.includes("CSHARP:") ||
		message.includes("C#") ||
		(data && typeof data === "object" && (data.action || data.requestId))
	) {
		// Logs de comunicação CorelDRAW/C# vão para o canal "csharp"
		channel = "csharp";
		isCsharpLog = true;
	}

	// Nível do .env (respeitado pelo console do Vite - npm run dev:full)
	const envLevel = getLogLevel();

	// Para logs de API e C# no canal SSE, SEMPRE usar nível 3 (DEBUG) para mostrar informações completas
	// Isso garante que npm run log:api e npm run log:csharp sempre mostrem tudo
	// Mas o console do Vite continua respeitando o nível do .env
	const sseLevel = isApiLog || isCsharpLog ? LogLevel.DEBUG : envLevel;

	// Filtrar health:pong - apenas nível 3 (DEBUG) loga health checks
	if (logCategory === LogCategory.HEALTH && envLevel !== LogLevel.DEBUG) {
		return;
	}

	// Verificar se action nos dados é health:pong (mesmo que categoria não seja HEALTH)
	if (
		data &&
		typeof data === "object" &&
		data.action === "health:pong" &&
		envLevel !== LogLevel.DEBUG
	) {
		return;
	}

	// Verificar se deve logar esta categoria no nível do .env (para console)
	if (!shouldLog(logCategory, envLevel)) {
		return;
	}

	// Formatar mensagem conforme o nível do .env (para console do Vite)
	const formatted = formatLogMessage(logCategory, message, data, envLevel);

	// Se a formatação retornou vazio, não logar
	if (!formatted.message) {
		return;
	}

	try {
		const originIsHttp =
			typeof window !== "undefined" &&
			window.location &&
			/^http/.test(window.location.origin);
		const base = originIsHttp ? window.location.origin : FRONTEND_URL;

		// Detectar se é log de debug HMAC (permite campos sensíveis no nível DEBUG)
		const isHmacDebugLog = message.includes("🔐") || message.includes("HMAC");
		const allowHmacDebugConsole = envLevel === LogLevel.DEBUG && isHmacDebugLog;
		const allowHmacDebugSSE = sseLevel === LogLevel.DEBUG && isHmacDebugLog;

		// Dados para o console do Vite (respeita nível do .env)
		let consoleData = formatted.data;
		if (consoleData) {
			consoleData = sanitizeLogData(consoleData, 0, allowHmacDebugConsole);
			// Aplicar filtros conforme nível do .env
			if (envLevel === LogLevel.MINIMAL) {
				consoleData = undefined;
			} else if (
				envLevel === LogLevel.OPERATIONAL &&
				logCategory === LogCategory.PAYLOAD
			) {
				consoleData = createPayloadSummary(consoleData);
			}
		}

		// Dados para o canal SSE (sempre completo para API/C#, respeita nível para outros)
		let sseData = formatted.data;
		if (isApiLog || isCsharpLog) {
			// Para API/C#, formatar novamente com nível DEBUG para ter dados completos
			const sseFormatted = formatLogMessage(
				logCategory,
				message,
				data,
				LogLevel.DEBUG,
			);
			sseData = sseFormatted.data;
			if (sseData) {
				sseData = sanitizeLogData(sseData, 0, allowHmacDebugSSE);
				// Sempre completo para API/C# - não aplicar reduções
			}
		} else {
			// Para outros logs, usar os mesmos dados do console (respeita nível)
			sseData = consoleData;
		}

		// Enviar dados do console (respeita nível do .env) para impressão no terminal do Vite
		// O vite.config.ts vai usar esses dados para o console e reemitir via SSE
		// Mas para API/C#, vamos enviar também os dados completos em um campo separado
		fetch(`${base}/api/terminal-log`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			mode: originIsHttp ? "cors" : "no-cors",
			body: JSON.stringify({
				channel,
				message: formatted.message,
				data: consoleData, // Dados para console (respeita nível do .env)
				sseData: isApiLog || isCsharpLog ? sseData : undefined, // Dados completos para SSE (apenas API/C#)
			}),
		}).catch(() => {
			// Zero fallback - se API não disponível, não logar nada
		});
	} catch {
		// Zero fallback - se houver erro, não logar nada
	}
};

/**
 * Canal dedicado para testes/sanduíche de logs (SSE via Vite middleware)
 * Apenas em desenvolvimento
 *
 * @param message Mensagem de log
 * @param data Dados opcionais (serão sanitizados automaticamente)
 * @param channel Canal do log (storage, api, csharp, etc.)
 * @param category Categoria do log (opcional, será detectada automaticamente se não fornecida)
 */
export const sendToTestLog = async (
	message: string,
	data?: any,
	channel: string = "storage",
	category?: LogCategory,
) => {
	// Verificação crítica: não enviar logs em produção
	if (!isDevelopmentMode()) {
		return;
	}

	// Detectar categoria se não fornecida
	const logCategory = category || detectLogCategory(message);
	const level = getLogLevel();

	// Filtrar health:pong - apenas nível 3 (DEBUG) loga health checks
	if (logCategory === LogCategory.HEALTH && level !== LogLevel.DEBUG) {
		return;
	}

	// Verificar se action nos dados é health:pong (mesmo que categoria não seja HEALTH)
	if (
		data &&
		typeof data === "object" &&
		data.action === "health:pong" &&
		level !== LogLevel.DEBUG
	) {
		return;
	}

	// Verificar se deve logar esta categoria no nível atual
	if (!shouldLog(logCategory, level)) {
		return;
	}

	// Formatar mensagem conforme o nível
	const formatted = formatLogMessage(logCategory, message, data, level);

	// Se a formatação retornou vazio, não logar
	if (!formatted.message) {
		return;
	}

	try {
		const originIsHttp =
			typeof window !== "undefined" &&
			window.location &&
			/^http/.test(window.location.origin);
		const base = originIsHttp ? window.location.origin : FRONTEND_URL;

		// Detectar se é log de debug HMAC (permite campos sensíveis no nível DEBUG)
		const isHmacDebugLog = message.includes("🔐") || message.includes("HMAC");
		const allowHmacDebug = level === LogLevel.DEBUG && isHmacDebugLog;

		// Sanitizar e formatar dados conforme nível
		let finalData = formatted.data;
		if (finalData) {
			finalData = sanitizeLogData(finalData, 0, allowHmacDebug);

			// Nível 1: sem dados (apenas mensagem)
			if (level === LogLevel.MINIMAL) {
				finalData = undefined;
			}
			// Nível 2: dados organizados (resumo + campos principais)
			else if (
				level === LogLevel.OPERATIONAL &&
				logCategory === LogCategory.PAYLOAD
			) {
				finalData = createPayloadSummary(finalData);
			}
			// Nível 3: dados completos (já vem formatado)
		}

		fetch(`${base}/api/test-log`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			mode: originIsHttp ? "cors" : "no-cors",
			body: JSON.stringify({
				channel,
				message: formatted.message,
				data: finalData,
			}),
		}).catch(() => { });
	} catch {
		// Fallback silencioso - não logar em produção
		if (isDevelopmentMode()) {
			try {
				// Detectar se é log de debug HMAC (permite campos sensíveis no nível DEBUG)
				const isHmacDebugLog =
					message.includes("🔐") || message.includes("HMAC");
				const allowHmacDebug = level === LogLevel.DEBUG && isHmacDebugLog;

				let finalData = formatted.data;
				if (finalData) {
					finalData = sanitizeLogData(finalData, 0, allowHmacDebug);

					// Nível 1: sem dados
					if (level === LogLevel.MINIMAL) {
						finalData = undefined;
					}
					// Nível 2: dados organizados
					else if (
						level === LogLevel.OPERATIONAL &&
						logCategory === LogCategory.PAYLOAD
					) {
						finalData = createPayloadSummary(finalData);
					}
				}
				console.log(`[TEST:${channel}]`, formatted.message, finalData);
			} catch { }
		}
	}
};

/**
 * Logger unificado - API principal para logging no projeto
 *
 * Use este objeto para todos os logs no projeto:
 * - logger.debug() - Detalhes de depuração
 * - logger.info() - Informações gerais
 * - logger.warn() - Avisos
 * - logger.error() - Erros
 *
 * Todos os logs são automaticamente:
 * - Bloqueados em produção
 * - Sanitizados (dados sensíveis removidos)
 * - Enviados para o terminal do backend (dev apenas)
 */
export const logger = {
	/**
	 * Log de debug - detalhes úteis para depuração
	 * Apenas no nível DEBUG (3)
	 */
	debug: (message: string, data?: any) => {
		if (!isDevelopmentMode()) return;

		// Verificar se deve logar no nível atual (apenas nível DEBUG)
		const level = getLogLevel();
		if (level !== LogLevel.DEBUG) return;

		debugLog(`🐛 [DEBUG] ${message}`, data);
		sendToTerminal(`🐛 [DEBUG] ${message}`, data);
	},

	/**
	 * Log informativo - informações gerais sobre o fluxo
	 * Apenas em nível OPERATIONAL (2) e DEBUG (3)
	 */
	info: (message: string, data?: any) => {
		if (!isDevelopmentMode()) return;

		// Info logs apenas em nível OPERATIONAL ou superior
		const level = getLogLevel();
		if (level < LogLevel.OPERATIONAL) return;

		debugLog(`ℹ️ [INFO] ${message}`, data);
		sendToTerminal(`ℹ️ [INFO] ${message}`, data);
	},

	/**
	 * Log de aviso - situações que merecem atenção mas não impedem o fluxo
	 */
	warn: (message: string, data?: any) => {
		if (!isDevelopmentMode()) return;
		debugLog(`⚠️ [WARN] ${message}`, data);
		sendToTerminal(`⚠️ [WARN] ${message}`, data);
	},

	/**
	 * Log de erro - erros e exceções
	 * IMPORTANTE: Erros críticos podem ser logados mesmo em produção (via console.error)
	 */
	error: (message: string, data?: any, critical: boolean = false) => {
		// Erros críticos sempre logam no console (mesmo em produção)
		if (critical) {
			console.error(
				`❌ [ERROR] ${message}`,
				data ? sanitizeLogData(data) : data,
			);
		}

		if (!isDevelopmentMode()) return;
		debugLog(`❌ [ERROR] ${message}`, data);
		sendToTerminal(`❌ [ERROR] ${message}`, data);
	},
};
